import { CheckoutConfigGratuityAmount, Money } from '@open-tender/types'

export interface TipSelection {
  tip: Money | null
  tipPercent?: Money | null
  tipCustomAmount?: Money | null
}

export interface TipSelectionUpdate {
  tip: Money | null
  tipPercent: Money | null
  tipCustomAmount: Money | null
}

// 'classify' normalizes local metadata only, never validates. 'restore' and
// 'recalculate' both resubmit a corrected amount and must revalidate.
export type TipUpdateKind = 'default' | 'recalculate' | 'restore' | 'classify'

export interface TipUpdateResult {
  kind: TipUpdateKind
  update: TipSelectionUpdate
}

export const shouldClearTip = (
  hasTip: boolean,
  selection: TipSelection
): boolean =>
  !hasTip &&
  (Boolean(selection.tip) ||
    selection.tipPercent != null ||
    Boolean(selection.tipCustomAmount))

export const resolveTipUpdate = (
  selection: TipSelection,
  tipOptions: CheckoutConfigGratuityAmount[],
  defaultOption: CheckoutConfigGratuityAmount | null
): TipUpdateResult | null => {
  // A known, still-configured percentage survives even if `tip` itself was
  // externally wiped (e.g. an unrelated updateForm call while check was
  // null). The default option is a separate source from tipOptions.
  if (selection.tipPercent) {
    const match =
      tipOptions.find((i) => i.percent === selection.tipPercent) ||
      (defaultOption && defaultOption.percent === selection.tipPercent
        ? defaultOption
        : null)
    if (match) {
      return match.amount !== selection.tip
        ? {
            kind: 'recalculate',
            update: {
              tip: match.amount,
              tipPercent: match.percent,
              tipCustomAmount: null,
            },
          }
        : null
    }
    // The brand no longer offers this percent. Don't leave tip paired with
    // stale percent metadata: re-classify whatever amount remains as custom,
    // or fall back to the default if nothing remains.
    return selection.tip
      ? {
          kind: 'classify',
          update: {
            tip: selection.tip,
            tipPercent: null,
            tipCustomAmount: selection.tip,
          },
        }
      : defaultOption
      ? {
          kind: 'default',
          update: {
            tip: defaultOption.amount,
            tipPercent: defaultOption.percent,
            tipCustomAmount: null,
          },
        }
      : {
          kind: 'classify',
          update: { tip: null, tipPercent: null, tipCustomAmount: null },
        }
  }

  // One-time classification for custom tips persisted before tipCustomAmount
  // existed: tip is set, tipPercent is explicitly null (custom), never shadowed.
  if (
    selection.tip &&
    selection.tipPercent === null &&
    selection.tipCustomAmount === undefined
  ) {
    return {
      kind: 'classify',
      update: {
        tip: selection.tip,
        tipPercent: null,
        tipCustomAmount: selection.tip,
      },
    }
  }

  // A known custom dollar amount survives and is restored exactly (a fixed
  // amount doesn't scale with subtotal), so no recalculation, just resubmission.
  if (
    selection.tipCustomAmount &&
    selection.tip !== selection.tipCustomAmount
  ) {
    return {
      kind: 'restore',
      update: {
        tip: selection.tipCustomAmount,
        tipPercent: null,
        tipCustomAmount: selection.tipCustomAmount,
      },
    }
  }

  if (!selection.tip) {
    return defaultOption
      ? {
          kind: 'default',
          update: {
            tip: defaultOption.amount,
            tipPercent: defaultOption.percent,
            tipCustomAmount: null,
          },
        }
      : null
  }

  if (selection.tipPercent === undefined) {
    const match =
      tipOptions.find((i) => i.amount === selection.tip) ||
      (defaultOption && defaultOption.amount === selection.tip
        ? defaultOption
        : null)
    return {
      kind: 'classify',
      update: {
        tip: selection.tip,
        tipPercent: match ? match.percent : null,
        tipCustomAmount: match ? null : selection.tip,
      },
    }
  }

  return null
}
